(ns practitest.frontend.forms3.custom-field-form-common
  (:require [practitest.frontend.events :as events]
            [practitest.frontend.forms3.entity-form.fields :as fields3]
            [practitest.frontend.components.forms :as forms-components]
            [practitest.frontend.components.list-builder :as list-builder]
            [practitest.frontend.components.confirmation :as confirmation]
            [practitest.frontend.components.modal :as modal]
            [practitest.frontend.components.notifications :as notifications]
            [practitest.frontend.components.selector :as selector]
            [practitest.frontend.common.tooltip :as common-tooltip]
            [practitest.frontend.components2.buttons :as buttons]
            [practitest.frontend.components2.confirmation-modal :refer [confirmation-modal]]
            [practitest.frontend.components2.spinners :refer [mux-spinner]]
            [practitest.frontend.components2.dropdown :refer [dropdown-button dropdown-menu-item]]
            [practitest.frontend.global-fields-settings.accordion :refer [accordion-list]]
            [practitest.frontend.global-fields-settings.common :as gf-common]
            [practitest.frontend.global-fields-settings.entity-linker :refer [entity-linker-table]]
            [practitest.frontend.common.i18n :refer [get-translation format-translation]]
            [practitest.frontend.common.mixpanel :as mixpanel]
            [clojure.string :as string]
            [reagent.core :as r]))

;; Shared Create/Edit field form composition, used by both Global Fields
;; (forms3.global-field-form) and Project Fields (forms3.project-field-form).
;;
;; This module owns the shared RENDERING shape only - which fields to show, in
;; what order, and how the values-list/entity-linker widgets are composed.

(defn fields-props
  "Field Name / Field Format [/ Parent List] field config array. name-field-type
   defaults to plain :text; Global Fields passes :glb-prefixed-text to show its
   globe-icon prefix (project-level fields have no such concept).
   name-editable? defaults to true; pass false to render the name as read-only."
  [{:keys [edit? field-format list-field-names on-clear-dependent-fields keys-cfg name-field-type name-editable?]}]
  (let [name-editable? (if (some? name-editable?) name-editable? true)]
  (into []
    (concat
      [{:label             (get-translation "global-fields.form-fields.name" "Field Name")
        :field-id          (:name keys-cfg)
        :type              (or name-field-type :text)
        :mandatory?        true
        :validators        [::forms-components/string-not-blank]
        :edit-permissions? name-editable?
        :max-length        30
        :field-name        (:name keys-cfg)}
       {:label             (get-translation "global-fields.form-fields.format" "Field Format")
        :field-id          (:format keys-cfg)
        :type              :list
        :mandatory?        (not edit?)
        :validators        [::forms-components/string-not-blank]
        :edit-permissions? (not edit?)
        :label-fn          (fn [item] (gf-common/get-field-format-label (:id item)))
        :possible-values   ["text" "memo" "number" "list" "date" "datetime" "checkbox" "user" "linkedlist" "multilist" "url"]
        :clear-linked-lists on-clear-dependent-fields
        :field-name        (:format keys-cfg)}]
      (when (= field-format "linkedlist")
        [{:label             "Parent List"
          :field-id          (:parent-list keys-cfg)
          :type              :list
          :mandatory?        true
          :validators        [::forms-components/string-not-blank]
          :edit-permissions? name-editable?
          :possible-values   list-field-names
          :clear-linked-lists on-clear-dependent-fields
          :field-name        (:parent-list keys-cfg)}])))))

(defn possible-values-list
  "Flat 'Values customization' list-builder, for list/multilist formats.
   Optionally renders a source-list selector above the list builder when
   source-list-options is non-empty (project fields only, not global fields)."
  [{:keys [form-props keys-cfg on-delete-item on-add-item on-edit-item always-editable-values?
           action-in-progress? is-global-field? source-list-options source-list on-source-list-change read-only?]}]
  (let [values-container [:div.g2-field-container
                          [:label.m-label.top (get-translation "global-fields.form-fields.values" "Values customization")]
                          [list-builder/view-panel (:possible-values keys-cfg) form-props on-delete-item on-add-item
                           nil on-edit-item action-in-progress? always-editable-values? read-only?]]]
    [:<>
     (when (seq source-list-options)
       [:div.g2-field-container
        [:div.source-list-label-row
         [:label.m-label (get-translation "custom-fields.source-list.label" "Take Values from Existing List")]
         [common-tooltip/info-circle-tooltip
          (get-translation "custom-fields.source-list.tooltip"
                           "When a source list is selected, this field inherits its values from the selected list.")]]
        [:div.source-list-selector
         [selector/new-select
          {:items      source-list-options
           :label-fn   :name
           :key-fn     :name
           :clearable? true
           :value      source-list
           :on-change  on-source-list-change}]]])
     (if read-only?
       [:div.read-only-section values-container]
       values-container)]))

(defn linked-values-list
  "Per-parent-value accordion of list-builders, for the linkedlist format."
  [{:keys [form-props keys-cfg parent-list-items on-delete-item on-add-item on-edit-item always-editable-values? action-in-progress? is-global-field?]}]
  (let [view-map (mapv (fn [parent-item]
                          {:id parent-item
                           :title parent-item
                           :body [list-builder/view-panel (:possible-values keys-cfg) form-props
                                  (on-delete-item parent-item) on-add-item (keyword parent-item)
                                  (on-edit-item parent-item) action-in-progress? always-editable-values? is-global-field?]})
                        parent-list-items)]
    (when (seq parent-list-items)
      [:div.g2-field-container
       [:label.m-label.top (get-translation "global-fields.form-fields.values" "Values customization")]
       [accordion-list view-map {:start-open? is-global-field?}]])))

(defn entity-linker-section
  "Optional 'Select Projects' checkbox (only rendered when show-select-projects?
   is truthy - Global Fields only) plus the entity-linker table."
  [{:keys [form-props keys-cfg show-select-projects? entity-linker-opts]}]
  (let [{:keys [get-field-state set-field-state]} form-props
        checkbox-id (:select-projects keys-cfg)]
    [:<>
     (when show-select-projects?
       [:div.g2-field-container
        [:label.m-label (get-translation "global-fields.form-fields.select-projects" "Select Projects")]
        [:div.projects-check
         [:input.m-checkbox {:type :checkbox
                             :checked (:value @(get-field-state checkbox-id))
                             :on-change (fn [e] (set-field-state checkbox-id {:value (.. e -target -checked)
                                                                              :dirty? true}))}]
         [:span (get-translation "global-fields.select-projects-info" "Enforce the Global Field to all projects")]]])
     [:div.g2-field-container
      [:label.m-label.top (get-translation "global-fields.form-fields.select-linkage" "Select linkage to Entities")]
      [entity-linker-table (:entity-table keys-cfg) form-props entity-linker-opts]]]))

(defn render-form-body
  "Composes Field Name/Format[/Parent List] -> Values customization -> Entity
   linkage. `ctx` is threaded as-is into possible-values-list/linked-values-list/
   entity-linker-section, so it should carry all the keys those need in addition
   to the ones read directly here."
  [{:keys [edit? field-format loading? fields-props-list form-props is-global-field? source-list] :as ctx}]
  (if loading?
    [:div.centered-spinner [mux-spinner]]
    [:div.global-field-form
     (doall (map (fn [props]
                   (with-meta [fields3/build-field props form-props]
                              {:key (:field-id props)}))
                 fields-props-list))
     (let [has-source?    (boolean (seq source-list))
           read-only?     (or is-global-field? has-source?)
           values-section (cond
                            (#{"list" "multilist"} field-format) [possible-values-list (assoc ctx :read-only? read-only?)]
                            (= field-format "linkedlist") [linked-values-list ctx])]
       (when values-section
         (if (and is-global-field? (= field-format "linkedlist"))
           [:div.read-only-section values-section]
           values-section)))
     [entity-linker-section ctx]]))

;; Convert Field Type - shared between Global Fields (also reused for its grid
;; row-menu actions) and Project Fields.

(defn get-convert-actions
  "target-format action list for the 'Convert Field Type' control, given the
   field's current format."
  [{:keys [field-format on-select-target mixpanel-category has-source-list?]}]
  (letfn [(action [label target mixpanel-action]
            {:label label
             :on-click (fn []
                         (mixpanel/track mixpanel-category {"Action" mixpanel-action})
                         (on-select-target target))})]
    (cond
      (= field-format "list")
      [(if has-source-list?
         {:label     (get-translation "global-fields.convert.ddl.to-text" "Convert to Text")
          :disabled? true
          :tooltip   (get-translation "custom-fields.convert.has-source-list.tooltip"
                                      "Cannot convert to text a field that takes its value from existing list")}
         (action (get-translation "global-fields.convert.ddl.to-text" "Convert to Text")
                 "text" "Field Convert To Text Clicked"))
       (action (get-translation "global-fields.convert.ddl.to-multilist" "Convert to Multi-List")
               "multilist" "Field Convert To MultiList Clicked")]

      (= field-format "text")
      [(action (get-translation "global-fields.convert.ddl.to-list" "Convert to List")
               "list" "Field Convert To List Clicked")]

      :else
      [{:label (get-translation "global-fields.convert.button" "Convert")
        :disabled? true
        :tooltip (get-translation "global-fields.convert.not-convertible.tooltip"
                                  "This field type cannot be converted")}])))

(defn convert-field-type-button
  "Dropdown button rendering `convert-actions` (see get-convert-actions).
   Returns nil when the caller passes an empty action list (e.g. Global Fields
   passes [] when its feature flag is off)."
  [{:keys [convert-actions]}]
  (when (seq convert-actions)
    (let [enabled? (some (complement :disabled?) convert-actions)]
      (into [dropdown-button
             {:text (get-translation "global-fields.convert.field-type-button" "Convert Field Type")
              :primary? true
              :disabled? (not enabled?)
              :tooltip (when-not enabled?
                         (get-translation "global-fields.convert.not-convertible.tooltip"
                                          "This field type cannot be converted."))}
             {:placement "bottom-end"}]
            (for [{:keys [label] :as action} convert-actions]
              ^{:key label}
              [dropdown-menu-item (-> action
                                       (dissoc :label)
                                       (assoc :text label))])))))

(defn form-header
  "Shared page-header shape: title + optional Convert Field Type button (only
   when editing)."
  [{:keys [title convert-button]}]
  [:div.view-header.global-field-form-header
   [:span title]
   convert-button])

(defn convert-confirmation-modal
  "Confirm-before-converting dialog. Body/title text is identical between
   Global Fields and Project Fields, since neither's convert has a different
   set of target formats or wording - only the dispatched event and mixpanel
   category differ, so those are passed in by the caller.
   Accepts an optional :warning string rendered as a second paragraph below the main body."
  [{:keys [convert-confirm? convert-target on-yes on-no mixpanel-category warning]}]
  (let [target-label (gf-common/get-field-format-label convert-target)
        body-text    (case convert-target
                       "text"      (get-translation "global-fields.convert.confirm.to-text.body"
                                                    "Are you sure you want to convert this field to Text?")
                       "multilist" (get-translation "global-fields.convert.confirm.to-multilist.body"
                                                    "Are you sure you want to convert this field to Multi-List?")
                       "list"      (get-translation "global-fields.convert.confirm.to-list.body"
                                                    "Are you sure you want to convert this field to List? Existing text values will become List options.")
                       nil)]
    (when convert-confirm?
      [confirmation/confirm
       {:show?     (atom true)
        :title     (format-translation
                     (get-translation "global-fields.convert.confirm.title" "Convert Field to %{format}")
                     :format target-label)
        :body      (if warning
                     (r/as-element [:<> [:p body-text] [:p warning]])
                     body-text)
        :no-label  (get-translation "generic.button.cancel" "Cancel")
        :yes-label (get-translation "global-fields.convert.button" "Convert")
        :on-yes    (fn [_]
                     (mixpanel/track mixpanel-category {"Action" "Field Convert Confirmed"})
                     (on-yes))
        :on-no     (fn [_]
                     (mixpanel/track mixpanel-category {"Action" "Field Convert Cancelled"})
                     (on-no))}])))

(defn format-field-values-to-string [field-format possible-values]
  (case field-format
    "linkedlist"
    (string/join ","
                 (map (fn [[parent-item items]]
                        ;; keyword->string, not name: (name :Web/Mobile) => "Mobile"
                        (str "{[" (if (keyword? parent-item) (events/keyword->string parent-item) (str parent-item))
                             "]:" (string/join "," items) "}"))
                      possible-values))
    ("list" "multilist")
    (string/join "," possible-values)
    nil))

(defn find-id-by-name [fields name]
  (some (fn [{:keys [label id]}]
          (when (= label name) id))
        fields))

(defn save-button-disabled? [form-data]
  (let [field-values       (vals form-data)
        dirty-fields       (filter :dirty? field-values)
        fields-with-errors (filter (fn [field] (not (empty? (:errors field)))) field-values)]
    (or (empty? dirty-fields)
        (seq fields-with-errors))))

(defn rename-value-modal
  "Shared rename-value dialog for Global Fields and Project Fields.
   count (optional) shows an affected-entity count; only Global Fields passes it."
  [{:keys [show? old-value new-value saving? count error on-cancel on-change on-submit]}]
  (let [disabled? (or (string/blank? new-value) (= new-value old-value))]
    [modal/modal-with-notifications
     {:class            "global-field-rename-value-modal"
      :is-open?         (boolean show?)
      :on-request-close (when-not saving? on-cancel)
      :close-button?    true
      :centered?        true}
     [:span (get-translation "global-field.rename-value.title" "Rename Value")]
     [:div.rename-value-modal-body
      [:div.g2-field-container
       [:label.m-label (get-translation "global-field.rename-value.current" "Current value")]
       [:div.current-value old-value]]
      [:div.g2-field-container
       [:label.m-label (get-translation "global-field.rename-value.new" "New value")]
       [:input.search-input {:value       (or new-value "")
                             :autoFocus   true
                             :disabled    saving?
                             :on-change   on-change
                             :on-key-down (fn [e] (when (and (= "Enter" (.-key e)) (not disabled?)) (on-submit)))}]]
      (when (some? count)
        [:p.affected-count
         (format-translation
          (get-translation "global-field.rename-value.affected"
                           "This will update %{count} entities across the account.")
          :count count)])
      [:div.rename-value-modal-footer.add-cancel-component
       [buttons/button {:size      "large"
                        :on-click  on-cancel
                        :disabled? saving?
                        :text      (get-translation "generic.button.cancel" "Cancel")}]
       [buttons/button {:primary?  true
                        :size      "large"
                        :disabled? disabled?
                        :loading?  saving?
                        :on-click  on-submit
                        :text      (get-translation "generic.button.rename" "Rename")}]]]
     (when error
       [(notifications/toast-element error {:type  "error"
                                            :id    "rename-value-error"
                                            :class "in-modal-msg-error"})])]))

(defn delete-possible-value-modal
  "Shared delete-confirmation modal for possible-value deletion.
   Caller provides modal-data map (delete-value-confirm? title value-used? value),
   an on-confirm callback receiving the value, and an on-cancel callback."
  [{:keys [modal-data on-confirm on-cancel]}]
  (let [{:keys [delete-value-confirm? title value-used? value]} modal-data]
    (when delete-value-confirm?
      [confirmation-modal
       {:show?                    true
        :show-footer?             true
        :title                    title
        :confirmation-button-text (get-translation "generic.button.delete-value" "Delete Value")
        :on-confirm               (fn [] (on-confirm value))
        :on-cancel                on-cancel
        :dialog-test-id           "delete-confirm-dialog"
        :confirm-test-id          "delete-confirm-btn"}
       [:<>
        (if value-used?
          [:p (get-translation "global-field.delete-possible-value.modal-body-note"
                               "Note: The value you selected is used in multiple entities. Deleting it will remove it from all of them, leaving empty fields. Any linked values will also be deleted.")]
          [:p (get-translation "global-field.delete-possible-value.modal-body-confirm" "Are you sure you want to delete this value?")])
        [:p (str (get-translation "global-field.delete-possible-value.value-to-delete" "Value to delete: ") value)]]])))

(defn convert-blocked-alert
  "Shows a modal when a field convert is rejected by the backend (blocked: true).
   Caller passes show?, title, message, usages, and on-cancel."
  [{:keys [show? title message usages on-cancel]}]
  (when show?
    [confirmation-modal
     {:show?        true
      :show-footer? false
      :title        title
      :on-cancel    on-cancel}
     [:div
      [:p message]
      (doall (for [item usages]
               ^{:key (:url item)}
               [:div.project-link
                [:a {:href (:url item) :target "_blank"} (:name item)]]))]]))
